import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { request, requestAll } from "./api";
import { keys } from "./liveEvents";
import type {
  Alert,
  AlertSeverity,
  AlertState,
  AlertSummary,
  AlertType,
  AuditLog,
  Command,
  CommandBatch,
  CommandBatchSummary,
  Device,
  DeviceGroup,
  DeviceLiveSessions,
  EnrollmentToken,
  FleetSettings,
  FleetSummary,
  Page,
  StoredFile,
  TelemetrySeries,
  User,
} from "./types";

// Live events keep the cached data current, so these intervals are a safety net for
// what no event announces (and for when the event stream is down), not the main way
// the screen stays fresh.
const SAFETY_NET_MS = 60_000;

export function useDevices() {
  return useQuery({
    queryKey: keys.devices,
    queryFn: () => requestAll<Device>("/devices"),
    refetchInterval: SAFETY_NET_MS,
  });
}

export function useDevice(id: string) {
  return useQuery({
    queryKey: keys.device(id),
    queryFn: () => request<Device>(`/devices/${id}`),
    refetchInterval: SAFETY_NET_MS,
  });
}

export function useGroups() {
  return useQuery({
    queryKey: keys.groups,
    queryFn: () => requestAll<DeviceGroup>("/groups"),
  });
}

export function useFleetSummary() {
  return useQuery({
    queryKey: keys.fleetSummary,
    queryFn: () => request<FleetSummary>("/fleet/summary"),
    refetchInterval: SAFETY_NET_MS,
  });
}

/** The screen viewing and audio capture running on a device, to join rather than double. */
export function useLiveSessions(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: keys.liveSessions(deviceId),
    queryFn: () => request<DeviceLiveSessions>(`/devices/${deviceId}/live`),
    enabled,
    refetchInterval: SAFETY_NET_MS,
  });
}

export function useFleetSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => request<FleetSettings>("/fleet/settings"),
    staleTime: Infinity,
  });
}

/** Every unresolved alert, for colouring devices and counting. */
export function useActiveAlerts() {
  return useQuery({
    queryKey: [...keys.alerts, "active"],
    queryFn: () => requestAll<Alert>("/alerts", { state: "active" }, 2000),
    refetchInterval: SAFETY_NET_MS,
  });
}

export function useAlertSummary() {
  return useQuery({
    queryKey: keys.alertSummary,
    queryFn: () => request<AlertSummary>("/alerts/summary"),
    refetchInterval: SAFETY_NET_MS,
  });
}

export interface AlertFilters {
  state: AlertState;
  type?: AlertType | "";
  severity?: AlertSeverity | "";
  deviceId?: string;
}

export function useAlerts(filters: AlertFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...keys.alerts, "list", filters, page, pageSize],
    queryFn: () =>
      request<Page<Alert>>("/alerts", {
        query: {
          state: filters.state,
          type: filters.type,
          severity: filters.severity,
          device_id: filters.deviceId,
          limit: pageSize,
          offset: page * pageSize,
        },
      }),
    placeholderData: keepPreviousData,
    refetchInterval: SAFETY_NET_MS,
  });
}

export function useDeviceCommands(deviceId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...keys.deviceCommands(deviceId), page, pageSize],
    queryFn: () =>
      request<Page<Command>>(`/devices/${deviceId}/commands`, {
        query: { limit: pageSize, offset: page * pageSize },
      }),
    placeholderData: keepPreviousData,
  });
}

export function useBatches(page: number, pageSize: number) {
  return useQuery({
    queryKey: [...keys.batches, page, pageSize],
    queryFn: () =>
      request<Page<CommandBatchSummary>>("/commands/batches", {
        query: { limit: pageSize, offset: page * pageSize },
      }),
    placeholderData: keepPreviousData,
    refetchInterval: SAFETY_NET_MS,
  });
}

export function useBatch(id: string | null) {
  return useQuery({
    queryKey: keys.batch(id ?? ""),
    queryFn: () => request<CommandBatch>(`/commands/batches/${id}`),
    enabled: id !== null,
    // Results arrive as events; this catches what happened before the page was open.
    refetchInterval: (query) => {
      const counts = query.state.data?.status_counts ?? {};
      const running = (counts.pending ?? 0) + (counts.sent ?? 0) + (counts.acked ?? 0);
      return running > 0 ? 10_000 : false;
    },
  });
}

export interface Range {
  /** Shown on the button. */
  label: string;
  hours: number;
}

export const RANGES: Range[] = [
  { label: "1 hour", hours: 1 },
  { label: "6 hours", hours: 6 },
  { label: "24 hours", hours: 24 },
  { label: "7 days", hours: 24 * 7 },
];

export function useTelemetrySeries(deviceId: string, hours: number) {
  return useQuery({
    queryKey: [...keys.telemetry(deviceId), "series", hours],
    queryFn: () => {
      const until = new Date();
      const since = new Date(until.getTime() - hours * 3600_000);
      return request<TelemetrySeries>(`/devices/${deviceId}/telemetry/series`, {
        query: { since: since.toISOString(), until: until.toISOString(), points: 240 },
      });
    },
    placeholderData: keepPreviousData,
    // The window moves with time, so the chart is fetched again regularly: often
    // for a short period, rarely for a long one.
    refetchInterval: Math.min(300_000, Math.max(15_000, (hours * 3600_000) / 240)),
  });
}

export function useUsers(enabled: boolean) {
  return useQuery({
    queryKey: keys.users,
    queryFn: () => requestAll<User>("/users"),
    enabled,
  });
}

export function useEnrollmentTokens(enabled: boolean) {
  return useQuery({
    queryKey: keys.enrollmentTokens,
    queryFn: () => requestAll<EnrollmentToken>("/enrollment-tokens"),
    enabled,
  });
}

export function useFiles(kind: "artifact" | "device_upload", filter: { commandId?: string } = {}) {
  return useQuery({
    queryKey: [...keys.files, kind, filter],
    queryFn: () =>
      requestAll<StoredFile>("/files", { kind, command_id: filter.commandId }, 1000),
  });
}

export function useAuditLog(page: number, pageSize: number, enabled: boolean) {
  return useQuery({
    queryKey: [...keys.audit, page, pageSize],
    queryFn: () =>
      request<Page<AuditLog>>("/audit-logs", {
        query: { limit: pageSize, offset: page * pageSize },
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
}
